/*
 * Connect Wardrobe configurator
 * IKEA-style "frame first, interiors second, one basket action" flow.
 * Reads a server-rendered JSON blob, manages build state in vanilla JS,
 * and adds the whole configuration to cart in a single /cart/add.js call.
 * No external libraries.
 */
if (!customElements.get('connect-configurator')) {
  class ConnectConfigurator extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-cc-data]');
      if (!dataEl) return;

      try {
        this.config = JSON.parse(dataEl.textContent);
      } catch (e) {
        return; // leave the no-JS markup in place
      }
      if (!this.config.widths || !this.config.widths.length) return;

      this.cartDrawer = document.querySelector('cart-drawer');
      this.moneyFormat = this.config.moneyFormat || '£{{amount}}';

      // State: selected width id + { addonKey: qty }
      this.state = { widthId: this.config.widths[0].id, qty: {} };

      this.cacheDom();
      this.bindEvents();
      this.selectWidth(this.state.widthId, true);
    }

    cacheDom() {
      this.widthEls = Array.from(this.querySelectorAll('[data-cc-width]'));
      this.galleryImgs = Array.from(this.querySelectorAll('[data-cc-gallery-img]'));
      this.galleryTabs = Array.from(this.querySelectorAll('[data-cc-gallery-tab]'));
      this.galleryPanels = Array.from(this.querySelectorAll('[data-cc-gallery-panel]'));
      this.addonsHost = this.querySelector('[data-cc-addons]');
      this.slotFill = this.querySelector('[data-cc-slot-fill]');
      this.slotLabel = this.querySelector('[data-cc-slot-label]');
      this.notice = this.querySelector('[data-cc-notice]');
      this.summaryList = this.querySelector('[data-cc-summary-list]');
      this.totalEl = this.querySelector('[data-cc-total]');
      this.payEl = this.querySelector('[data-cc-pay]');
      this.atc = this.querySelector('[data-cc-atc]');
      this.errorEl = this.querySelector('[data-cc-error]');
      this.schematic = this.querySelector('[data-cc-schematic]');
      this.summary = this.querySelector('[data-cc-summary]');
    }

    bindEvents() {
      this.widthEls.forEach((el) => {
        el.addEventListener('click', () => this.selectWidth(el.dataset.ccWidth));
      });

      this.galleryTabs.forEach((tab) => {
        tab.addEventListener('click', () => this.selectGalleryTab(tab.dataset.ccGalleryTab));
      });

      if (this.atc) this.atc.addEventListener('click', () => this.addToCart());

      const toggle = this.querySelector('[data-cc-summary-toggle]');
      if (toggle && this.summary) {
        toggle.addEventListener('click', () => {
          const open = this.summary.classList.toggle('is-expanded');
          toggle.textContent = open ? 'Hide build' : 'View build';
        });
      }
    }

    get width() {
      return this.config.widths.find((w) => w.id === this.state.widthId);
    }

    selectWidth(id, initial) {
      this.state.widthId = id;
      const width = this.width;

      this.widthEls.forEach((el) => {
        el.setAttribute('aria-pressed', String(el.dataset.ccWidth === id));
      });

      // Swap hero gallery image to reflect the width.
      this.galleryImgs.forEach((img) => {
        img.classList.toggle('is-active', img.dataset.ccGalleryImg === id);
      });

      // Clamp any quantities that exceed the new frame's caps / slots.
      let clamped = false;
      const nextQty = {};
      width.addons.forEach((addon) => {
        const prev = this.state.qty[addon.key] || 0;
        const capped = Math.min(prev, addon.available ? addon.max : 0);
        if (capped !== prev) clamped = true;
        nextQty[addon.key] = capped;
      });
      this.state.qty = nextQty;
      // A second pass clamps against shared slot capacity.
      if (this.clampToSlots()) clamped = true;

      this.renderAddons();
      this.render();

      if (clamped && !initial) {
        this.showNotice(`Adjusted to fit the ${width.label} frame`);
      } else {
        this.showNotice(null);
      }

      if (!initial) {
        this.track('configurator_width_selected', { width: width.label });
      }
    }

    selectGalleryTab(name) {
      this.galleryTabs.forEach((t) => t.setAttribute('aria-selected', String(t.dataset.ccGalleryTab === name)));
      this.galleryPanels.forEach((p) => { p.hidden = p.dataset.ccGalleryPanel !== name; });
    }

    /* Build the add-on cards for the active width. */
    renderAddons() {
      const width = this.width;
      this.addonsHost.innerHTML = '';

      width.addons.forEach((addon) => {
        const qty = this.state.qty[addon.key] || 0;
        const card = document.createElement('div');
        card.className = 'cc-addon' + (addon.available ? '' : ' is-unavailable');
        card.dataset.ccAddon = addon.key;

        const dims = typeof addon.dimensions === 'string' ? addon.dimensions.trim() : '';
        const details = dims || addon.lifestyle
          ? `<details class="cc-addon__disclosure">
               <summary>Details</summary>
               <div class="cc-addon__details">
                 ${dims ? `<div>${dims}</div>` : ''}
                 ${addon.lifestyle ? `<img src="${addon.lifestyle}" alt="${addon.title} interior" loading="lazy">` : ''}
               </div>
             </details>`
          : '';

        const control = addon.available
          ? `<div class="cc-stepper" role="group" aria-label="${addon.title} quantity">
               <button type="button" class="cc-stepper__btn" data-cc-dec aria-label="Remove one ${addon.title}">−</button>
               <span class="cc-stepper__val" data-cc-val>${qty}</span>
               <button type="button" class="cc-stepper__btn" data-cc-inc aria-label="Add one ${addon.title}">+</button>
             </div>`
          : '<span class="cc-addon__unavailable-note">Currently unavailable — notify me</span>';

        card.innerHTML = `
          ${addon.image ? `<img class="cc-addon__img" src="${addon.image}" alt="${addon.title}" loading="lazy">` : '<span class="cc-addon__img"></span>'}
          <div>
            <p class="cc-addon__name">${addon.title}</p>
            <span class="cc-addon__price">${this.money(addon.price)} each</span>
            ${details}
          </div>
          ${control}`;

        const inc = card.querySelector('[data-cc-inc]');
        const dec = card.querySelector('[data-cc-dec]');
        if (inc) inc.addEventListener('click', () => this.changeQty(addon.key, 1));
        if (dec) dec.addEventListener('click', () => this.changeQty(addon.key, -1));

        this.addonsHost.appendChild(card);
      });
    }

    changeQty(key, delta) {
      const addon = this.width.addons.find((a) => a.key === key);
      if (!addon || !addon.available) return;

      const current = this.state.qty[key] || 0;
      let next = current + delta;
      if (next < 0) next = 0;
      if (next > addon.max) next = addon.max;

      // Respect shared slot capacity when increasing.
      if (delta > 0 && this.slotsUsedExcept(key) + next * addon.slotCost > this.width.slots) {
        next = Math.floor((this.width.slots - this.slotsUsedExcept(key)) / addon.slotCost);
      }
      if (next === current) return;

      this.state.qty[key] = next;
      this.showNotice(null);
      this.render();

      if (delta > 0) {
        this.track('configurator_addon_added', { item: addon.title, quantity: next });
      }
    }

    slotsUsedExcept(skipKey) {
      return this.width.addons.reduce((sum, a) => {
        if (a.key === skipKey) return sum;
        return sum + (this.state.qty[a.key] || 0) * a.slotCost;
      }, 0);
    }

    slotsUsed() {
      return this.slotsUsedExcept(null);
    }

    clampToSlots() {
      let changed = false;
      // Reduce quantities (in declared order) until within slot budget.
      this.width.addons.slice().reverse().forEach((addon) => {
        while (this.slotsUsed() > this.width.slots && (this.state.qty[addon.key] || 0) > 0) {
          this.state.qty[addon.key] -= 1;
          changed = true;
        }
      });
      return changed;
    }

    render() {
      const width = this.width;
      const used = this.slotsUsed();
      const full = used >= width.slots;

      // Steppers: reflect values + disable at caps / slot limit.
      this.width.addons.forEach((addon) => {
        const card = this.addonsHost.querySelector(`[data-cc-addon="${addon.key}"]`);
        if (!card) return;
        const qty = this.state.qty[addon.key] || 0;
        const val = card.querySelector('[data-cc-val]');
        if (val) val.textContent = qty;
        const inc = card.querySelector('[data-cc-inc]');
        const dec = card.querySelector('[data-cc-dec]');
        if (dec) dec.disabled = qty <= 0;
        if (inc) {
          const noSlots = used + addon.slotCost > width.slots;
          inc.disabled = qty >= addon.max || noSlots;
        }
      });

      // Slot meter.
      if (this.slotFill) this.slotFill.style.width = `${Math.min(100, (used / width.slots) * 100)}%`;
      if (this.slotLabel) {
        this.slotLabel.textContent = full
          ? 'Frame full — remove an item to add more'
          : `${used} of ${width.slots} interior slots used`;
      }

      this.renderSchematic(width, used);
      this.renderSummary(width);
    }

    renderSummary(width) {
      const rows = [];
      let total = width.price;
      rows.push({ label: `1× Frame ${width.label}`, amount: width.price });

      width.addons.forEach((addon) => {
        const qty = this.state.qty[addon.key] || 0;
        if (qty > 0) {
          const line = addon.price * qty;
          total += line;
          rows.push({ label: `${qty}× ${addon.title}`, amount: line });
        }
      });

      if (this.summaryList) {
        this.summaryList.innerHTML = rows.map((r) =>
          `<li class="cc-summary__row"><span>${r.label}</span><span>${this.money(r.amount)}</span></li>`
        ).join('');
      }
      if (this.totalEl) this.totalEl.textContent = this.money(total);

      // Clearpay fed the *configured* total.
      if (this.payEl) {
        const four = (total / 4 / 100).toFixed(2);
        this.payEl.innerHTML = `Clearpay: 4 payments of £${four}`;
      }

      this.total = total;
    }

    /* Tier 2: layered SVG schematic. The carcass ships with a fixed top shelf
       and hanging rail (standard); below them, drawer units always stack at the
       bottom (each a chest of N drawer fronts) and add-on shelves sit above. */
    renderSchematic(width, used) {
      if (!this.schematic) return;
      const slots = width.slots;
      const W = 220;
      const H = 360;
      const pad = 16;
      const innerH = H - pad * 2;
      const x = pad + 6;
      const w = W - pad * 2 - 12;
      const drawersPerUnit = this.config.drawersPerUnit || 2;
      const hasShelf = this.config.standardTopShelf;
      const hasRail = this.config.standardRail;

      // Reserve a top band for the standard shelf + rail; add-ons fill below it.
      const topReserve = hasShelf || hasRail ? innerH * 0.2 : 0;
      const areaH = innerH - topReserve;
      // Hanging clearance below the rail: extra empty rows where no shelf can go.
      const clearance = this.config.hangingClearance != null ? this.config.hangingClearance : 2;
      const slotH = areaH / (slots + clearance);
      const baseY = H - pad; // interior floor

      // Standard fixtures (drawn last so they sit on top).
      let standard = '';
      if (hasShelf) {
        const ys = pad + topReserve * 0.62;
        standard += `<line x1="${x}" y1="${ys}" x2="${x + w}" y2="${ys}" stroke="#2c2820" stroke-width="2"/>`;
      }
      if (hasRail) {
        const yr = pad + topReserve * 0.85;
        standard += `<line x1="${x + 10}" y1="${yr}" x2="${x + w - 10}" y2="${yr}" stroke="#2c2820" stroke-width="2"/>`;
        const n = 5;
        for (let i = 0; i < n; i += 1) {
          const hx = x + 20 + (i * (w - 40)) / (n - 1);
          standard += `<circle cx="${hx}" cy="${yr}" r="1.6" fill="#2c2820"/>`;
          standard += `<path d="M ${hx} ${yr} L ${hx - 6} ${yr + 12} L ${hx + 6} ${yr + 12} Z" fill="none" stroke="#2c2820" stroke-width="1.1"/>`;
        }
      }

      // Peg-hole columns down both inner walls marking the shelf slot positions.
      let pegs = '';
      const pegLeft = pad + 4;
      const pegRight = W - pad - 4;
      for (let i = 0; i <= slots; i += 1) {
        const py = baseY - i * slotH;
        pegs += `<circle cx="${pegLeft}" cy="${py}" r="1.1" fill="#c4bcae"/>`;
        pegs += `<circle cx="${pegRight}" cy="${py}" r="1.1" fill="#c4bcae"/>`;
      }

      // Split the build: drawer units (bottom) vs shelf slots (above).
      const drawerUnits = [];
      let shelfSlots = 0;
      width.addons.forEach((addon) => {
        const qty = this.state.qty[addon.key] || 0;
        if (addon.kind === 'drawer') {
          for (let i = 0; i < qty; i += 1) drawerUnits.push(addon.slotCost);
        } else {
          shelfSlots += qty * addon.slotCost;
        }
      });

      let layers = '';
      let slotIndex = 0; // counted up from the floor

      // Drawer chests at the bottom.
      drawerUnits.forEach((cost) => {
        const unitH = cost * slotH - 4;
        const yTop = baseY - (slotIndex + cost) * slotH + 2;
        layers += `<g class="cc-slot-layer is-filled">`;
        layers += `<rect x="${x}" y="${yTop}" width="${w}" height="${unitH}" fill="none" stroke="#2c2820" stroke-width="2"/>`;
        const frontH = unitH / drawersPerUnit;
        for (let d = 0; d < drawersPerUnit; d += 1) {
          const fy = yTop + d * frontH;
          if (d > 0) {
            layers += `<line x1="${x}" y1="${fy}" x2="${x + w}" y2="${fy}" stroke="#2c2820" stroke-width="1"/>`;
          }
          // drawer handle
          layers += `<line x1="${W / 2 - 12}" y1="${fy + frontH / 2}" x2="${W / 2 + 12}" y2="${fy + frontH / 2}" stroke="#2c2820" stroke-width="2"/>`;
        }
        layers += `</g>`;
        slotIndex += cost;
      });

      // Shelves above the drawers.
      for (let s = 0; s < shelfSlots; s += 1) {
        const y = baseY - (slotIndex + 1) * slotH + slotH / 2;
        layers += `<g class="cc-slot-layer is-filled">
          <line x1="${x}" y1="${y}" x2="${W - pad - 6}" y2="${y}" stroke="#2c2820" stroke-width="2"/>
        </g>`;
        slotIndex += 1;
      }

      this.schematic.innerHTML =
        `<svg viewBox="0 0 ${W} ${H}" role="img" aria-label="${used} of ${slots} slots filled">
          <rect x="${pad}" y="${pad}" width="${W - pad * 2}" height="${innerH}" fill="none" stroke="#2c2820" stroke-width="3"/>
          ${pegs}
          ${layers}
          ${standard}
        </svg>`;
    }

    showNotice(msg) {
      if (!this.notice) return;
      this.notice.hidden = !msg;
      if (msg) this.notice.textContent = msg;
    }

    /* ---------- Cart ---------- */
    buildItems() {
      const width = this.width;
      const buildId = `cw-${Math.floor(Date.now() / 1000)}`;
      const partOf = `${this.config.frameTitle} ${width.label}`;
      const items = [{
        id: width.variantId,
        quantity: 1,
        properties: this.cleanProps({ _build_id: buildId, Delivery: this.config.delivery })
      }];

      width.addons.forEach((addon) => {
        const qty = this.state.qty[addon.key] || 0;
        if (qty > 0 && addon.available) {
          items.push({
            id: addon.variantId,
            quantity: qty,
            properties: this.cleanProps({ _build_id: buildId, 'Part of': partOf })
          });
        }
      });
      return items;
    }

    cleanProps(obj) {
      const out = {};
      Object.keys(obj).forEach((k) => { if (obj[k]) out[k] = obj[k]; });
      return out;
    }

    async addToCart() {
      if (this.atc.classList.contains('is-loading')) return;
      this.setError(null);
      this.atc.classList.add('is-loading');
      this.atc.disabled = true;

      const items = this.buildItems();

      try {
        const res = await fetch(`${window.theme.routes.cartAdd}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({
            items,
            sections: 'cart-icon-bubble',
            sections_url: window.location.pathname
          })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');

        this.track('configurator_add_to_cart', {
          width: this.width.label,
          value: this.total / 100,
          items: items.map((i) => ({ id: i.id, quantity: i.quantity }))
        });
        this.fireGa4AddToCart(items);

        // Update the header count bubble from the add response.
        if (data.sections && data.sections['cart-icon-bubble']) {
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble) bubble.innerHTML = data.sections['cart-icon-bubble'];
        }

        if (window.theme.settings.afterAtc === 'page') {
          window.location.href = window.theme.routes.cart;
          return;
        }

        // Refresh the drawer contents and open it via the theme's own events,
        // so the build appears immediately without a page reload.
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        if (this.cartDrawer) {
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
            detail: { opener: this.atc }
          }));
        }

        this.atc.classList.add('is-success');
      } catch (err) {
        this.setError(err.message);
      } finally {
        this.atc.classList.remove('is-loading');
        this.atc.disabled = false;
        setTimeout(() => this.atc.classList.remove('is-success'), 1400);
      }
    }

    setError(msg) {
      if (!this.errorEl) return;
      this.errorEl.hidden = !msg;
      if (msg) this.errorEl.textContent = msg;
    }

    /* ---------- Analytics ---------- */
    track(event, payload) {
      if (typeof window.dataLayer !== 'undefined') {
        window.dataLayer.push({ event, ...payload });
      }
      this.dispatchEvent(new CustomEvent(`cc:${event}`, { bubbles: true, detail: payload }));
    }

    fireGa4AddToCart(items) {
      if (typeof window.gtag !== 'function') return;
      const all = [this.width].concat(this.width.addons.filter((a) => (this.state.qty[a.key] || 0) > 0));
      window.gtag('event', 'add_to_cart', {
        currency: this.config.currency || 'GBP',
        value: this.total / 100,
        items: items.map((i, idx) => ({
          item_id: i.id,
          item_name: all[idx] ? (all[idx].title || all[idx].label) : undefined,
          quantity: i.quantity
        }))
      });
    }

    /* ---------- Money ---------- */
    money(cents) {
      const value = (cents / 100).toFixed(2);
      const [pounds, pence] = value.split('.');
      const withCommas = pounds.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return this.moneyFormat
        .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${pence}`)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${withCommas},${pence}`);
    }
  }

  customElements.define('connect-configurator', ConnectConfigurator);
}
